import {
    ANALYTICS_ENDPOINT,
    ANALYTICS_MAX_QUEUED_MESSAGES,
    ANALYTICS_SUBMIT_DEBOUNCE_MS
} from "../Constants";
import {delayDebounced} from "./Utils";
import {makeUniqueSelector} from "./makeUniqueSelector";
import {Dispatchable} from "../../stem-core/src/base/Dispatcher";

export const AnalyticsEventType = {
    PAGE_VIEW: {key: "pageView", flush: true},
    POPUP_PAGE_CHANGE: {key: "popupPageChange", flush: true},
    JS_ERROR: {key: "jsError", flush: true, now: true},
    FLOW_START: {key: "flowStart", flush: true},
    JOURNEY_SELECTED: {key: "journeySelected", flush: false},
    ELEMENT_CLICK: {key: "elementClick", flush: true},
    SOCIAL_LOGIN_BUTTON_CLICKED: {key: "socialLoginButtonClicked", flush: true},
    REQUEST_EMAIL_ACCESS_CODE: {key: "requestEmailAccessCode", flush: true},
    ATTEMPT_CONFIRM_EMAIL_CODE: {key: "attemptConfirmEmailCode", flush: true},
    ATTEMPT_DONATE: {key: "attemptDonate", flush: true},
    ATTEMPT_SUBSCRIBE: {key: "attemptSubscribe", flush: true},
    ATTEMPT_NEWSLETTER_SIGNUP: {key: "attemptNewsletterSignup", flush: true},
    SUBSCRIPTION_SELECTED: {key: "subscriptionSelected", flush: true},
    SHOW_SUBSCRIPTION_COVERAGE_CARDS: {key: "showSubscriptionCoverageCards", flush: true},
    DONATE_SELECTED_AMOUNT: {key: "donateSelectedAmount", flush: true},
    IFRAME_COLLAPSED: {key: "iframeCollapsed", flush: true},
    USER_LOGIN: {key: "userLogin", flush: true},
    AJAX_REQUEST_FAILED: {key: "ajaxRequestFailed", flush: false},
    AJAX_REQUEST_SUCCEEDED: {key: "ajaxRequestSucceeded", flush: false},
    TAB_FOCUS: {key: "tabFocus", flush: false},
    TAB_DEFOCUS: {key: "tabDefocus", flush: false},
    TAB_CLOSE: {key: "tabClose", flush: true, now: true},
    TAB_OPEN: {key: "tabOpen", flush: false},
    RUN_USER_JOURNEY_ACTION: {key: "runUserJourneyAction", flush: true},
    FLOW_CHECKPOINT: {key: "flowCheckpoint", flush: true},
    CUSTOM: {key: "custom", flush: true}, // Custom merchant event
};

export const EventSender = {
    sdk: "sdk",
    wallet: "wallet",
    panel: "panel",
};

let analyticsClient = null;

export class AnalyticsClient extends Dispatchable {
    constructor(origin, sendWindowEvents) {
        super();

        analyticsClient = this;

        this.origin = origin;
        this.sendWindowEvents = sendWindowEvents;
        this.eventQueue = []; // TODO @branch rename to queue
        this.environment = {};
        this.identity = {};
        this.preprocessors = [];

        // TODO @branch maybe simplify this, or put in a try catch
        this.submitEnqueuedEventsDebounced = delayDebounced(
            () => this.submitEnqueuedEvents(),
            ANALYTICS_SUBMIT_DEBOUNCE_MS);

        if (this.sendWindowEvents) {
            this.attachWindowEventListeners();
        }
    }

    attachWindowEventListeners() {
        let haveSentTabClose; // We only want to send the TAB_CLOSE event once
        const sendTabCloseEvent = () => {
            if (!haveSentTabClose) {
                dispatchAnalyticsEvent(AnalyticsEventType.TAB_CLOSE);
                haveSentTabClose = 1;
            }
        };

        this.attachEventListener(window, "beforeunload", sendTabCloseEvent);
        this.attachEventListener(window, "unload", sendTabCloseEvent);

        this.attachEventListener(document, "visibilitychange", () => {
            // if (document.visibilityState === "visible") {
            //     dispatchAnalyticsEvent(AnalyticsEventType.TAB_FOCUS);
            // } else {
            //     dispatchAnalyticsEvent(AnalyticsEventType.TAB_DEFOCUS);
            // }
        });
    }

    submitEnqueuedEvents(onlyXHR) {
        // TODO @branch investigate and implement Do Not Track better
        if (this.eventQueue.length === 0) {
            return;
        }

        const serializePayload = (method) => JSON.stringify({
            method,
            origin: this.origin,
            identity: this.identity,
            events: this.eventQueue,
        });

        // TODO @branch, maybe we need to revisit this
        // if (!onlyXHR && navigator.sendBeacon) {
        //     const payload = serializePayload("beacon");
        //     if (navigator.sendBeacon(ANALYTICS_ENDPOINT, payload)) {
        //         this.eventQueue = [];
        //         return;
        //     }
        // }

        const payload = serializePayload("xhr");
        this.eventQueue = [];
        const xhr = new XMLHttpRequest();
        xhr.open("POST", ANALYTICS_ENDPOINT, true);
        xhr.send(payload);
    }

    enqueueEvent(eventType, payload = {}) {
        const globalEvent = self.event;
        // If the event is dispatched because of a user action, attach information about the user action.
        if (globalEvent) {
            let target = globalEvent.currentTarget || globalEvent.target;
            // In case this is React, which attaches all listeners to the document for some reason.
            if (target === document) {
                target = globalEvent.target || globalEvent.currentTarget;
            }
            if (target?.tagName) {
                payload.domEventType = globalEvent.type;
                if (target.dataset && target.dataset.blinkAnid) {
                    payload.domEventTargetAnid = target.dataset.blinkAnid;
                } else {
                    // If we don't have an analytics ID for this element, add at least selector instead.
                    payload.domEventTargetSelector = makeUniqueSelector(target);
                }
            }
        }
        let event = {
            type: eventType.key,
            timestamp: +Date.now() / 1000.0,
            localSessionTime: performance.now() / 1000.0,
            // TODO @branch put in the userId here??
            payload: {
                ...this.environment,
                ...payload,
            },
        };
        for (const preprocessor of this.preprocessors) {
            event = preprocessor(event) || event;
        }
        // Intentionally make a copy of identity here, since this object is sent outside our SDK,
        // and accidental modification in the merchant's code shouldn't interfere with the payload
        // of our analytics events.
        this.dispatch("event", {
            origin: this.origin,
            identity: {...this.identity},
            ...event,
        });
        this.eventQueue.push(event);
        if (this.eventQueue.length >= ANALYTICS_MAX_QUEUED_MESSAGES) {
            this.submitEnqueuedEvents();
        } else if (eventType.flush) {
            if (eventType.now) {
                this.submitEnqueuedEvents(1);
            } else {
                this.submitEnqueuedEventsDebounced();
            }
        }
    }

    updateEnvironment(data) {
        Object.assign(this.environment, data);
    }

    updateIdentity(data) {
        Object.assign(this.identity, data);
    }
}

export function dispatchAnalyticsEvent(type, payload = {}) {
    try {
        analyticsClient?.enqueueEvent(type, payload);
    } catch (e) {
        console.error("Blink analytics caught an error:", e);
    }
}

// Useful for tests.
export function cleanupAnalyticsClient() {
    analyticsClient?.cleanup();
    analyticsClient = null;
}

export function handleFirstJSError(message, file, line, col, error) {
    self.onerror = null;
    // TODO @branch use a non-global one if needed
    analyticsClient = analyticsClient || new AnalyticsClient("errorHandler");
    analyticsClient?.enqueueEvent(AnalyticsEventType.JS_ERROR, {
        message,
        file,
        line,
        col,
        error: {
            message: error?.message.slice(0, 256),
            stack: error?.stack.slice(0, 6000),
            timestamp: self.event?.timestamp,
        }
    });
}
